if (!customElements.get('ia-bundle-buy')) {
  class IaBundleBuy extends HTMLElement {
    constructor() {
      super();
      this.hotspots = this.querySelectorAll('.ia-bundle-buy__hotspot');
      this.items = this.querySelectorAll('.ia-bundle-buy__item');
      this.addButton = this.querySelector('[data-bundle-add]');
      this.errorElement = this.querySelector('[data-error-message]');
      this.init();
    }

    init() {
      this.hotspots.forEach(hotspot => {
        hotspot.addEventListener('click', () => this.activateByProductId(hotspot.dataset.productId));
      });

      this.items.forEach(item => {
        item.addEventListener('click', () => this.activateByProductId(item.dataset.productId));
      });

      // Individual-variants mode: a single button adds every configured variant at once.
      if (this.addButton) {
        this.addButton.addEventListener('click', this.handleBundleAdd.bind(this));
      }
    }

    activateByProductId(productId) {
      if (!productId) return;

      this.hotspots.forEach(hotspot => {
        hotspot.classList.toggle('is-active', hotspot.dataset.productId === productId);
      });

      this.items.forEach(item => {
        item.classList.toggle('is-active', item.dataset.productId === productId);
      });
    }

    // Reads the variants to add straight from the DOM at click time, so the cart
    // always reflects what is currently rendered (e.g. after a theme re-render).
    collectItems() {
      const cartItems = window.iaCartAPI?.cart?.items || [];
      const inCart = id => cartItems.some(item => String(item.variant_id) === String(id));
      return Array.from(this.querySelectorAll('[data-variant-id]'))
        .map(el => ({
          id: el.dataset.variantId,
          quantity: parseInt(el.dataset.variantQuantity, 10) || 1,
          gwp: el.dataset.gwp === 'true',
          sectionGift: el.dataset.sectionGift === 'true',
        }))
        // Drop gifts that are already in the cart so they never stack.
        .filter(item => item.id && !(item.gwp && inCart(item.id)));
    }

    // Build the cart/add payload. The bundle members (spot products) and the
    // section-level gift share a unique _bundle_id; the gift also records how many
    // members it requires (_bundle_count) and is flagged _gwp_bundle, so the cart
    // revokes it if any member is later removed (see removeIncompleteBundleGifts
    // in ia-cart.js). Per-product metafield gifts (gwp but not sectionGift) are
    // added unchanged.
    buildPayload(rawItems, bundleId) {
      const memberCount = rawItems.filter(item => !item.gwp).length;
      return rawItems.map(item => {
        const payload = { id: item.id, quantity: item.quantity };
        if (item.sectionGift) {
          payload.properties = { _bundle_id: bundleId, _gwp_bundle: '1', _bundle_count: String(memberCount) };
        } else if (!item.gwp) {
          payload.properties = { _bundle_id: bundleId };
        }
        return payload;
      });
    }

    async handleBundleAdd(event) {
      event.preventDefault();
      if (this.addButton.classList.contains('adding')) return;

      const rawItems = this.collectItems();
      if (!rawItems.length) return;

      this.toggleError(false);
      this.addButton.classList.add('adding');

      try {
        const api = window.iaCartAPI;
        if (!api || typeof api.addItemsToCart !== 'function') {
          throw new Error('Cart is not available. Please refresh and try again.');
        }
        // One id per click ties this bundle's members to its gift.
        const bundleId = String(api.getTimestamp?.() ?? Date.now());
        try {
          await api.addItemsToCart(this.buildPayload(rawItems, bundleId));
        } catch (error) {
          // A gift may have gone out of stock — retry with the non-gift items so
          // the bundle still adds. If there were no gifts, surface the error.
          const main = rawItems.filter(item => !item.gwp);
          if (main.length && main.length !== rawItems.length) {
            await api.addItemsToCart(this.buildPayload(main, bundleId));
          } else {
            throw error;
          }
        }
        document.querySelector('ia-cart')?.open();
      } catch (error) {
        this.toggleError(true, error?.message);
      } finally {
        this.addButton.classList.remove('adding');
      }
    }

    toggleError(show, message) {
      if (!this.errorElement) return;
      this.errorElement.classList.toggle('ia-hidden', !show);
      if (show && message) {
        const target = this.errorElement.querySelector('p') || this.errorElement;
        target.textContent = message;
      }
    }
  }

  customElements.define('ia-bundle-buy', IaBundleBuy);
}
